import * as React from 'react'

import type {
  BooleanFilterCategory,
  DataTableFilter,
  DataTableSource,
  DatePickerFilter,
  EntityFilterCategory,
  IdFilterCategory,
  IntegerFilterCategory,
  StringFilterCategory,
} from 'generated/resources/datatable.graphql'

import { FILTER_OPERATIONS } from 'modules/core/components/DataTable/constants'
import type { Filter } from 'modules/core/components/DataTable/types'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'

function getDefaultValue<T>(category: DataTableFilter<T>['categories'][number]) {
  switch (category.operation) {
    case FILTER_OPERATIONS.BETWEEN:
      return [undefined, undefined]
    default:
      return undefined
  }
}

function buildFilter<T>(filter: DataTableFilter<T>): Filter<T> {
  const category = filter.categories.at(0) as DataTableFilter<T>['categories'][number]

  return {
    filter,
    category: filter.categories.at(0) as DataTableFilter<T>['categories'][number],
    value: filter.categories.at(0)?.value ?? getDefaultValue(category),
  }
}

function getKey(tableIdentifier: string) {
  return `datatable-filters-${tableIdentifier}`
}

function getInitialFilters<T>(filters?: Array<DataTableFilter<T>>) {
  if (!filters) {
    return []
  }

  return filters.filter((filter) => filter.default).map(buildFilter)
}

function toPersistedFilter<T>(filter: Filter<T>): PersistedFilters<T> {
  return {
    identifier: filter.filter.identifier,
    kind: filter.category.kind,
    operation: filter.category.operation,
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    value: filter.value as ReturnType<typeof getDefaultValue>,
  }
}

function getFiltersJSON<T>(filters: Filter<T>[]) {
  // Clean noAutoOpen property before persisting to localStorage
  const cleanFilters = filters.map((filter) => {
    if ('noAutoOpen' in filter) {
      const { noAutoOpen: _, ...cleanFilter } = filter
      const leFilter: Filter<T> = cleanFilter

      return leFilter
    }
    return filter
  })
  return JSON.stringify(cleanFilters.map(toPersistedFilter))
}
function persistFilterChanges<T>(tableIdentifier: string, filtersJSON: string) {
  const key = getKey(tableIdentifier)
  localStorage.setItem(key, filtersJSON)
}

type PersistedFilters<T> = {
  identifier: string
  kind: DataTableFilter<T>['categories'][number]['kind']
  operation: DataTableFilter<T>['categories'][number]['operation']
  value: ReturnType<typeof getDefaultValue>
}

function getPersistedFilters<T>(
  tableIdentifier: string,
  configFilters?: Array<DataTableFilter<T>>
) {
  if (!configFilters) {
    return []
  }

  const key = getKey(tableIdentifier)
  const filtersJSON = localStorage.getItem(key)
  const filters: PersistedFilters<T>[] = filtersJSON ? JSON.parse(filtersJSON) : []

  const persistedFilters = []

  for (const filter of filters) {
    const configFilter = configFilters.find(
      (configFilter) => configFilter.identifier === filter.identifier
    )

    if (!configFilter) {
      continue
    }

    // This assertion is needed because TypeScript is dumb and types it as a union of several arrays
    const category = (
      configFilter.categories as (
        | DatePickerFilter
        | StringFilterCategory
        | IntegerFilterCategory
        | IdFilterCategory
        | BooleanFilterCategory
        | EntityFilterCategory
      )[]
    ).find((category) => category.kind === filter.kind && category.operation === filter.operation)

    if (!category) {
      continue
    }

    persistedFilters.push({
      filter: configFilter,
      category,
      value: filter.value,
    })
  }

  if (!persistedFilters.length) {
    return getInitialFilters(configFilters)
  }

  // If there's some change in the configuration of the filters of the DataTable, persisted filters
  // are gonna be ignored.
  const persistedFiltersIds = persistedFilters.map((filter) => filter.filter.identifier)
  const availableFiltersIds = configFilters.map((filter) => filter.identifier)
  const persistedFiltersIncludeConfigFilters = persistedFiltersIds.every((id) =>
    availableFiltersIds.includes(id)
  )

  if (!persistedFiltersIncludeConfigFilters) {
    return getInitialFilters(configFilters)
  }

  // Some filters may be added conditionally. If the filter list includes fixed filters,
  // they usually gets added to the persisted filters list before dynamic filters are ready.
  // We need to ensure that any missing default filters are added to the persisted filters list when available.
  const missingDefaultFilters = configFilters
    .filter((filter) => filter.default && !persistedFiltersIds.includes(filter.identifier))
    .map((filter) => {
      const data = buildFilter(filter)
      return {
        filter,
        category: data.category,
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        value: data.value as ReturnType<typeof getDefaultValue>,
      }
    })

  return persistedFilters.concat(missingDefaultFilters)
}

function getAvailableFilters<T>(
  shownFilters: Array<Filter<T>>,
  filtersProp?: Array<DataTableFilter<T>>
): Filter<T>[] {
  if (!filtersProp) {
    return []
  }

  return filtersProp
    .filter(
      (filter) =>
        !filter.default &&
        !shownFilters.find((shownFilter) => shownFilter.filter.identifier === filter.identifier)
    )
    .map(buildFilter)
}

type State<T> = {
  filters: Filter<T>[]
  availableFilters: Filter<T>[]
}

type Action<T> =
  | {
      type: 'add'
      filter: Filter<T>
      tableIdentifier: string
    }
  | {
      type: 'remove'
      filter: Filter<T>
      tableIdentifier: string
    }
  | {
      type: 'update'
      filter: Filter<T>
      tableIdentifier: string
    }
  | {
      type: 'reset'
      filters: DataTableFilter<T>[]
      tableIdentifier: string
    }
  | {
      type: 'sync'
      filters: Filter<T>[]
      tableIdentifier: string
    }

const reducer = <U>(state: State<U>, action: Action<U>) => {
  let filters: Filter<U>[]

  switch (action.type) {
    case 'add':
      filters = [...state.filters, action.filter]
      return {
        filters,
        availableFilters: state.availableFilters.filter(
          (availableFilter) => availableFilter.filter.identifier !== action.filter.filter.identifier
        ),
      }
    case 'remove':
      filters = state.filters.filter(
        (filter) => filter.filter.identifier !== action.filter.filter.identifier
      )
      return {
        filters,
        availableFilters: [
          ...state.availableFilters,
          {
            ...action.filter,
            value: undefined,
          },
        ],
      }
    case 'update':
      filters = state.filters.map((filter) => {
        if (filter.filter.identifier === action.filter.filter.identifier) {
          return action.filter
        }

        return filter
      })
      return { ...state, filters }
    case 'reset':
      filters = getInitialFilters(action.filters)

      return {
        filters: getInitialFilters(action.filters),
        availableFilters: getAvailableFilters([], action.filters),
      }
    case 'sync':
      const newFiltersConfigs = action.filters
      const newFilterIds = new Set(newFiltersConfigs.map(({ filter }) => filter.identifier))

      const currentFiltersWithoutRemovedOnes = state.filters.filter((filter) =>
        newFilterIds.has(filter.filter.identifier)
      )
      const currentAvailableFiltersWithoutRemovedOnes = state.availableFilters.filter((filter) =>
        newFilterIds.has(filter.filter.identifier)
      )

      const existingFilterIds = new Set(
        currentFiltersWithoutRemovedOnes.map((f) => f.filter.identifier)
      )
      const existingAvailableFilterIds = new Set(
        currentAvailableFiltersWithoutRemovedOnes.map((f) => f.filter.identifier)
      )

      const newFilters = newFiltersConfigs.filter(
        ({ filter }) =>
          !existingFilterIds.has(filter.identifier) &&
          !existingAvailableFilterIds.has(filter.identifier)
      )

      newFilters.forEach((filter) => {
        if (filter.filter.default || filter.value) {
          currentFiltersWithoutRemovedOnes.push(filter)
        } else {
          currentAvailableFiltersWithoutRemovedOnes.push(filter)
        }
      })

      return {
        filters: currentFiltersWithoutRemovedOnes,
        availableFilters: currentAvailableFiltersWithoutRemovedOnes,
      }
  }
}

interface UseFiltersReducerProps<T, U, S> {
  source: DataTableSource<T, S>
  onChangeFilterCallback: (filter?: Filter<U>, value?: unknown) => void
  incomingFilters?: Array<DataTableFilter<U>>
  persistFilters: boolean
}
export default function useFiltersReducer<T, U, S>({
  source,
  onChangeFilterCallback,
  incomingFilters,
  persistFilters,
}: UseFiltersReducerProps<T, U, S>) {
  // Mini-hack to only run these two methods once, otherwise we are calling them on every render
  const initialFiltersState = useLazyRef(() =>
    persistFilters
      ? getPersistedFilters(source.identifier, incomingFilters)
      : getInitialFilters(incomingFilters)
  ).current

  const initialAvailableFiltersState = useLazyRef(() =>
    getAvailableFilters(initialFiltersState, incomingFilters)
  ).current

  const [{ filters, availableFilters }, dispatch] = React.useReducer(reducer<U>, {
    filters: initialFiltersState,
    availableFilters: initialAvailableFiltersState,
  })

  useEffect(() => {
    let initialFilters: Filter<U>[] = []
    const persisted = getPersistedFilters(source.identifier, incomingFilters)
    const allFilters = (incomingFilters || []).map((f) => buildFilter(f))
    const persistedKeys = new Set(persisted.map(({ filter }) => filter.identifier))

    initialFilters =
      persisted.length > 0
        ? allFilters.filter(({ filter }) => !persistedKeys.has(filter.identifier)).concat(persisted)
        : allFilters

    dispatch({
      type: 'sync',
      filters: initialFilters,
      tableIdentifier: source.identifier,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingFilters?.map((f) => f.identifier).join('#'), source.identifier])

  const filtersJSON = getFiltersJSON(filters)

  useEffect(() => {
    const key = getKey(source.identifier)
    const previousfiltersJSON = localStorage.getItem(key)
    let previousFilters: PersistedFilters<T>[] = previousfiltersJSON
      ? JSON.parse(previousfiltersJSON)
      : []
    const availableFiltersIds = new Set(availableFilters.flatMap(({ filter }) => filter.identifier))
    const newFiltersIdentifiers = new Set(filters.map(({ filter }) => filter.identifier))

    previousFilters = previousFilters.filter((prev) => !availableFiltersIds.has(prev.identifier))
    const remaningFilters = previousFilters.filter((f) => !newFiltersIdentifiers.has(f.identifier))

    if (!persistFilters && previousFilters.length > 0) {
      persistFilterChanges(source.identifier, JSON.stringify([]))

      return
    }

    if (persistFilters) {
      persistFilterChanges(
        source.identifier,
        JSON.stringify(filters.map(toPersistedFilter).concat(remaningFilters))
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    availableFilters.map(({ filter }) => filter.identifier).join('#'),
    filtersJSON,
    persistFilters,
    source.identifier,
  ])

  const onAddFilter = (filter: Filter<U>) => {
    dispatch({ type: 'add', filter, tableIdentifier: source.identifier })
    onChangeFilterCallback(filter)
  }

  const onRemoveFilter = (filter: Filter<U>) => {
    dispatch({ type: 'remove', filter, tableIdentifier: source.identifier })
    onChangeFilterCallback(filter, null)
  }

  const onUpdateFilter = (filter: Filter<U>) => {
    dispatch({ type: 'update', filter, tableIdentifier: source.identifier })
    onChangeFilterCallback(filter)
  }

  const onResetFilters = () => {
    dispatch({ type: 'reset', filters: incomingFilters ?? [], tableIdentifier: source.identifier })
    onChangeFilterCallback()
  }

  return {
    filters,
    availableFilters,
    onAddFilter,
    onRemoveFilter,
    onUpdateFilter,
    onResetFilters,
  }
}
